import { Platform } from 'react-native';
import type { ReviewResponse } from '../types';

export type ReviewResponseServiceDependencies = {
  offlineReviewService: any;
  onlineApi: {
    updateResponse(
      reviewId: number,
      itemId: number,
      data: Partial<ReviewResponse>
    ): Promise<ReviewResponse>;
    updateSubmittedResponse(
      reviewId: number,
      itemId: number,
      data: Partial<ReviewResponse>
    ): Promise<ReviewResponse>;
  };
  getIsOnline: () => boolean;
  useOfflineWorkflow?: () => boolean;
  isWeb?: () => boolean;
};

export class ReviewResponseService {
  constructor(private readonly deps: ReviewResponseServiceDependencies) {}

  async updateResponse(
    reviewId: number,
    itemId: number,
    data: Partial<ReviewResponse>,
    options?: { forceOnline?: boolean }
  ): Promise<ReviewResponse> {
    try {
      if (!options?.forceOnline && (this.deps.useOfflineWorkflow?.() || !this.deps.getIsOnline())) {
        return await this.updateOfflineResponse(reviewId, itemId, data);
      }

      return await this.deps.onlineApi.updateResponse(reviewId, itemId, data);
    } catch (error) {
      console.error('[ReviewService] Error updating response online:', error);

      if (options?.forceOnline || this.isWeb()) {
        throw error;
      }

      try {
        return await this.updateOfflineResponse(reviewId, itemId, data);
      } catch (offlineError) {
        console.error('[ReviewService] ❌ Offline response update failed:', offlineError);
        throw error;
      }
    }
  }

  async updateSubmittedResponse(
    reviewId: number,
    itemId: number,
    data: Partial<ReviewResponse>
  ): Promise<ReviewResponse> {
    if (this.deps.useOfflineWorkflow?.()) {
      return this.updateOfflineResponse(reviewId, itemId, data);
    }

    return this.deps.onlineApi.updateSubmittedResponse(reviewId, itemId, data);
  }

  private async updateOfflineResponse(
    reviewId: number,
    itemId: number,
    data: Partial<ReviewResponse>
  ): Promise<ReviewResponse> {
    if (this.deps.offlineReviewService) {
      return await this.deps.offlineReviewService.updateOfflineResponse(reviewId, itemId, data);
    }

    throw new Error('Offline service not available');
  }

  private isWeb(): boolean {
    return this.deps.isWeb ? this.deps.isWeb() : Platform.OS === 'web';
  }
}
